import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Paper, Typography, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid } from '@mui/material';
// import Link from 'next/link';
import { dataConstants } from '../../../constants';
// import { getTabData } from '../../../helpers';
import formulaUtil from '../../../helpers/formula';
import groupBy from 'lodash.groupby';
// import D3Chart from '../../_common/D3Chart';

import LoadingItem from '../../_common/Skeleton/stock';
import dynamic from 'next/dynamic';

import YieldCurveChart from './YieldCurveChart';
import { getRandomStr } from '../../../helpers';
import NoData from '../../_common/NoData';
// import HistoricalYields from './HistoricalYields';
// import InversionHistory from './InversionHistory';
// import ComparedToWeek from './ComparedToWeek';
// import ComparedToDay from './ComparedToDay';
// import D3Chart, { InitialiseChart, loadData } from './D3Chart'

const HistoricalYields = dynamic(() => import('./HistoricalYields'),
  { loading: () => <LoadingItem /> })
const InversionHistory = dynamic(() => import('./InversionHistory'),
  { loading: () => <LoadingItem /> })
const ComparedToWeek = dynamic(() => import('./ComparedToWeek'),
  { loading: () => <LoadingItem /> })
const ComparedToDay = dynamic(() => import('./ComparedToDay'),
  { loading: () => <LoadingItem /> })

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  height: '100%',
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(132 135 154 / 20%), 0 2px 3px rgb(132 135 154 / 10%)',
  border: theme.palette.mode === 'dark' ? '1px solid rgb(19, 47, 76)' : 0,
  '& .col-md-4:hover button, .share-wrapper': {
    visibility: 'visible'
  },
  '& .Item-Head': {
    background: '#fbfbfc',
    borderBottom: '1px solid #eae9ee',
    marginBottom: 0,
    padding: theme.spacing(2),
    borderRadius: '4px 4px 0 0',
    '& a': {
      textDecoration: 'none',
      color: '#333',
      fontWeight: 600,
    },
    '& .option-title': {
      fontWeight: 600,
      color: '#13113c',
      fontSize: '.875rem',
    }
  },
  '& .section': {
    // minHeight: '3.4rem',
    borderBottom: '1px solid #eae9ee',
    borderColor: '#eae9ee',
    padding: '0 10px 10px 10px',
    verticalAlign: 'middle',
    background: '#fbfbfc',
    fontSize: '875rem',
    '& .option-title': {
      color: '#000000 !important',
      fontSize: '.875rem',
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      // fontWeight: '700 !important'
    },
    '& button, .share-wrapper': {
      visibility: 'hidden'
    },
    '.option-desc': {
      color: '#333 !important',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  '& .MuiGrid-root': {
    width: '100% !important',
    margin: 0,
  },
  '& .MuiGrid-root > .MuiGrid-item': {
    padding: 0,
    marginLeft: 0,
    marginRight: 0,
  },
}));

const getYieldTbl = (chart, difference) => {
  const { ySeries, series } = chart || {};
  let { sequenceData } = chart || {};
  const codes = formulaUtil.getSymbolCodes(ySeries);
  sequenceData = sequenceData && sequenceData.length > 0 && sequenceData?.filter(d => codes.includes(d?.code));
  const codeWiseData = groupBy(sequenceData, d => d?.code);
  const cols = sequenceData && codeWiseData && codeWiseData[sequenceData[0]?.code].map(d => d?.desc);

  const propertyNames = Object.keys(codeWiseData);

  const differences = difference ? codeWiseData[propertyNames[0]].map((val, i) => {
    const yesterday = codeWiseData[propertyNames[1]]?.[i];
    if (yesterday) {
      return {
        code: "YCURVE.DIFFERENCE",
        seq: val.seq,
        value: val.value - yesterday.value,
        desc: val.desc
      }
    }
  }).filter(x => x) : [];


  return (
    <TableContainer>
      <Table size="small" aria-label="yield table"
        sx={{
          '& th': {
            color: 'text.color.white',
            fontWeight: 600,
            background: '#fbfbfc',
            borderBottom: '1px solid #eae9ee',
          },
          '& td': {
            color: '#434166',
            fontSize: '.875rem !important'
          },
        }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ p: '6px' }}>Category</TableCell>
            {cols && cols.length && cols.map(d => (
              <TableCell sx={{ p: '6px' }} key={d}>{d}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(codeWiseData).map(k => {
            const s = series.find(d => d.yformula === k);
            return (
              <TableRow key={getRandomStr(3)} sx={{ background: k === "YCURVE.TODAY" && !difference ? '#fbfbfc' : 'transparent' }} hover>
                <TableCell sx={{ p: '6px', fontWeight: s?.legend?.label.includes("Today") && !difference ? 600 : 500 }}>{s ? s.legend.label : ''}</TableCell>
                {cols.map(desc => {
                  const obj = codeWiseData[k].find(d => d.desc === desc);
                  return <TableCell sx={{ p: '6px', fontWeight: k === "YCURVE.TODAY" && !difference ? 600 : 500 }} key={desc}>{obj ? obj.value : ''}</TableCell>
                })}
              </TableRow>
            )
          })}
          {difference &&
            <TableRow sx={{ background: '#fbfbfc' }} hover>
              <TableCell sx={{ p: '6px', fontWeight: 600 }}>Difference</TableCell>
              {differences.map(d => {
                return <TableCell sx={{ p: '6px', fontWeight: 600 }} key={d?.desc}>{d ? d.value?.toFixed(2) : ''}</TableCell>
              })}
            </TableRow>}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const YieldCurve = ({ data, themes, getChartData, adStyle }) => {
  const { ECO_SECTION_CODES } = dataConstants || {};
  const yData = {};

  const dataArr = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_DATA) ? data.filter(d => d.section_code === ECO_SECTION_CODES.YIELD_DATA) : undefined;
  // const yieldData = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_DATA) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_DATA) : undefined;
  // //yieldData = yieldData && yieldData.tabData && yieldData.tabData.length > 0 ? yieldData.tabData : undefined;

  if (dataArr) {
    dataArr.forEach(d => {
      yData[d.settings && d.settings.dataSetName ? d.settings.dataSetName : d.section_code] = (d.tabData || []);
    });
  }


  const yieldC = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_CHART) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_CHART) : undefined;
  // const yieldChart = yieldC && yieldC.tabData && yieldC.tabData.length > 0 ? getChartData(yieldC.tabData[0], getTabData(yData, yieldC, ECO_SECTION_CODES.YIELD_DATA), 'sequenceData') : undefined;

  const yieldWeek = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_CHART) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_WEEK_CHART) : undefined;
  // const yieldWeekChart = yieldWeek && yieldWeek.tabData && yieldWeek.tabData.length > 0 ? getChartData(yieldWeek.tabData[0], getTabData(yData, yieldWeek, ECO_SECTION_CODES.YIELD_DATA), 'sequenceData') : undefined;

  const yieldYest = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_YEST_CHART) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_YEST_CHART) : undefined;
  // const yieldYestChart = yieldYest && yieldYest.tabData && yieldYest.tabData.length > 0 ? getChartData(yieldYest.tabData[0], getTabData(yData, yieldYest, ECO_SECTION_CODES.YIELD_DATA), 'sequenceData') : undefined;

  const yieldInverBelly = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_INVER_BELLY_CHART) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_INVER_BELLY_CHART) : undefined;
  // const yieldInverBellyChart = yieldInverBelly && yieldInverBelly.tabData && yieldInverBelly.tabData.length > 0 ? getChartData(yieldInverBelly.tabData[0], getTabData(yData, yieldInverBelly, ECO_SECTION_CODES.YIELD_DATA)) : undefined;

  const yieldInverHist = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_INVER_HIST_CHART) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_INVER_HIST_CHART) : undefined;
  // const yieldInverHistChart = yieldInverHist && yieldInverHist.tabData && yieldInverHist.tabData.length > 0 ? getChartData(yieldInverHist.tabData[0], getTabData(yData, yieldInverHist, ECO_SECTION_CODES.YIELD_DATA)) : undefined;

  // const yieldInverLong = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_INVER_LONG_CHART) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_INVER_LONG_CHART) : undefined;
  // const yieldInverLongChart = yieldInverLong && yieldInverLong.tabData && yieldInverLong.tabData.length > 0 ? getChartData(yieldInverLong.tabData[0], getTabData(yData, yieldInverLong, ECO_SECTION_CODES.YIELD_DATA)) : undefined;

  // const yieldExp2000 = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_EXP_2000_CHART) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_EXP_2000_CHART) : undefined;
  // const yieldExp2000Chart = yieldExp2000 && yieldExp2000.tabData && yieldExp2000.tabData.length > 0 ? getChartData(yieldExp2000.tabData[0], getTabData(yData, yieldExp2000, ECO_SECTION_CODES.YIELD_DATA), 'sequenceData') : undefined;

  // const yieldExp2008 = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_EXP_2008_CHART) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_EXP_2008_CHART) : undefined;
  // const yieldExp2008Chart = yieldExp2008 && yieldExp2008.tabData && yieldExp2008.tabData.length > 0 ? getChartData(yieldExp2008.tabData[0], getTabData(yData, yieldExp2008, ECO_SECTION_CODES.YIELD_DATA), 'sequenceData') : undefined;

  // const yieldRate10Y = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_RATES_10_CHART) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_RATES_10_CHART) : undefined;
  // const yieldRate10YChart = yieldRate10Y && yieldRate10Y.tabData && yieldRate10Y.tabData.length > 0 ? getChartData(yieldRate10Y.tabData[0], getTabData(yData, yieldRate10Y, ECO_SECTION_CODES.YIELD_DATA)) : undefined;

  // const yieldRate1Y = data && data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_RATES_1_CHART) ? data.find(d => d.section_code === ECO_SECTION_CODES.YIELD_RATES_1_CHART) : undefined;
  // const yieldRate1YChart = yieldRate1Y && yieldRate1Y.tabData && yieldRate1Y.tabData.length > 0 ? getChartData(yieldRate1Y.tabData[0], getTabData(yData, yieldRate1Y, ECO_SECTION_CODES.YIELD_DATA)) : undefined;

  // const yieldTbl = getYieldTbl(yieldChart);

  const currentDate = new Date();
  const fiveDaysAgo = new Date(currentDate);
  fiveDaysAgo?.setDate(currentDate?.getDate() - 5);

  const dateModified = fiveDaysAgo?.toISOString() ?? "";

  return (
    <NoData noData={!data && !data?.length > 0} >
      <Box className="yield-section" sx={{ my: 1 }}>
        {yieldC &&
          <Item className="row sum-r yield-main">
            <div className="col-md-9">
              <Box className="yield-mainchart">
                <Box className="Item-Head">
                  <Typography component="h2" className="option-title" sx={{ mr: 'auto' }} >
                    US Treasury Yield Curve
                  </Typography>
                </Box>
                <YieldCurveChart
                  data={data}
                  getChartData={getChartData}
                  yData={yData}
                  yieldC={yieldC}
                  themes={themes}
                  getYieldTbl={getYieldTbl}
                />
              </Box>
            </div>
          </Item>}
        {(yieldWeek || yieldYest) &&
          <Item className="row sum-r yield-finer" sx={{ my: 1.5 }}>
            <div className="col-md-12">
              <Box className="Finer Analysis chart" >
                <Box
                  className="Item-Head" sx={{ borderBottom: '0 !important' }}>
                  <Typography component="h3" className="option-title" sx={{ mr: 'auto' }} >
                    Analysing US Yield Curve Movement
                  </Typography>
                  <Typography variant="caption" className="option-title-desc" sx={{ mr: 'auto', mt: 1, color: '#333' }}>
                    {`Comparing today’s yield curve with the yields of the previous day and the previous week.`}
                  </Typography>
                </Box>
                <Box component="div" className="option-content">
                  <Box component="div" className="Finer Analysis-chart">
                    <Grid container spacing={0} sx={{ my: 0, flexWrap: { md: 'nowrap', xs: 'wrap' } }}>
                      <Grid size={{ md: 6, xs: 12, }}>
                        <ComparedToWeek
                          data={data}
                          getChartData={getChartData}
                          yData={yData}
                          yieldWeek={yieldWeek}
                          themes={themes}
                          getYieldTbl={getYieldTbl}
                        />
                      </Grid>
                      <Grid size={{ md: 6, xs: 12, }}>
                        <ComparedToDay
                          data={data}
                          getChartData={getChartData}
                          yData={yData}
                          yieldYest={yieldYest}
                          themes={themes}
                          getYieldTbl={getYieldTbl}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            </div>
          </Item>}
        {(yieldInverBelly || yieldInverHist) &&
          <Item className="row sum-r yield-invert" sx={{ my: 1.5 }}>
            <div className="col-md-12">
              <Box className="Finer Analysis chart">
                <Box
                  className="Item-Head" sx={{ borderBottom: '0 !important' }}>
                  <Typography component="h3" className="option-title" sx={{ mr: 'auto' }} >
                    Inversion and Steepness of the Yield Curve
                  </Typography>
                  <Typography variant="caption" className="option-title-desc" sx={{ mr: 'auto', mt: 1, color: '#333' }}>
                    {`A normal yield curve slopes upward as the long-term treasury bonds or bonds with longer maturities typically yield higher returns due to higher risk associated with their investments. However, if investors are concerned about an economic downturn in the future, they rush to park their money in safer long-term bonds. At this point, yields start to fall and the spread between the long-term yields and the short-term yields becomes narrower. Steepness in the curve is observed during economic expansion, when the long-end is markedly higher than the short-end.`}
                  </Typography>
                </Box>
                <Box component="div" className="option-content">
                  <Box component="div" className="Finer Analysis-chart">
                    <Grid container spacing={0} sx={{ my: 0, flexWrap: { md: 'nowrap', xs: 'wrap' } }}>
                      <Grid size={{ md: 6, xs: 12, }}>
                        <HistoricalYields
                          data={data}
                          getChartData={getChartData}
                          yData={yData}
                          yieldInverBelly={yieldInverBelly}
                          themes={themes}
                        />
                      </Grid>
                      <Grid size={{ md: 6, xs: 12, }}>
                        <InversionHistory
                          data={data}
                          getChartData={getChartData}
                          yData={yData}
                          yieldInverHist={yieldInverHist}
                          themes={themes}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            </div>
          </Item>}
        {/* {
        (yieldExp2000 || yieldExp2008) &&
        <div className="row sum-r yield-shape">
          <div className="col-md-12">
            <Box className="Finer Analysis chart" sx={{ my: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  mb: 0.5,
                  px: 0.5,
                }}
              >
                <Typography component="h4" className="option-title" sx={{ mr: 'auto' }} >
                  Shape of Yield Curve - Historical Examples
                </Typography>
              </Box>
                <Box component="div" className="option-content">
                <Typography variant="caption" className="option-title-desc" sx={{ mr: 'auto', my: 2 }}>
                  {`The slope of the yield curve has been a strong predictor of recessions in the past. During the Dotcom Recession (2001) and the Great Financial Recession (2007-2009), the yield curves were inverted at the beginning of the recession, then progressed towards flat and normal slopes in the subsequent years.`}
                </Typography>
                <Box component="div" className="Finer Analysis-chart">
                  <Grid container spacing={3} sx={{ my: 0 }}>
                    <Grid item size={{md: 6, xs: 12,  }}>
                      {yieldExp2000 && yieldExp2000Chart !== undefined &&
                        <Box component="div" className="col-md-6">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              mb: 2,
                              px: 0.5,
                            }}
                          >
                            <Typography component="h4" className="option-title" sx={{ mr: 'auto' }} >
                              {yieldExp2000.section_name}
                            </Typography>
                          </Box>
                          <Box component="div" className="option-content">
                            <D3Chart
                              showChart={true}
                              noHeader
                              // maxHeight={chartProps.maxHeight}
                              scale={1}
                              // onClick={code => handleClick(code)}
                              chart={{ ...yieldExp2000Chart, id: yieldExp2000.section_code }}
                              // userName={`@${chart.code.split('@')[1]}`}
                              themes={themes}
                              type={dataConstants.CHART_STATE.VIEW}
                              chartMargin={{ bottom: 48 }}
                              // axisTextMargin={chartProps.axisTextMargin}
                              //chartMargin={{left: 40, bottom: 48 }}
                              //axisTextMargin={{ y: 96 }}
                              // wrapperClass="StockCardImage"
                              //showLegend={false}
                              showCrosshair
                            />
                          </Box>
                        </Box>}
                    </Grid>
                    <Grid item size={{md: 6, xs: 12,  }}>
                      {(yieldExp2008 && yieldExp2008Chart !== undefined) &&
                        <Box component="div" className="col-md-6">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              mb: 2,
                              px: 0.5,
                            }}
                          >
                            <Typography component="h4" className="option-title" sx={{ mr: 'auto' }} >
                              {yieldExp2008.section_name}
                            </Typography>
                          </Box>
                          <Box component="div" className="option-content">
                            <D3Chart
                              showChart={true}
                              noHeader
                              // maxHeight={chartProps.maxHeight}
                              scale={1}
                              // onClick={code => handleClick(code)}
                              chart={{ ...yieldExp2008Chart, id: yieldExp2008.section_code }}
                              // userName={`@${chart.code.split('@')[1]}`}
                              themes={themes}
                              type={dataConstants.CHART_STATE.VIEW}
                              chartMargin={{ bottom: 48 }}
                              // axisTextMargin={chartProps.axisTextMargin}
                              //chartMargin={{left: 40, bottom: 48 }}
                              //axisTextMargin={{ y: 96 }}
                              // wrapperClass="StockCardImage"
                              //showLegend={false}
                              showCrosshair
                            />
                          </Box>
                        </Box>}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          </div>
        </div>
      } */}
        <div itemScope itemType="https://schema.org/FAQPage">
          <Item className="row sum-r yield-faq" sx={{ my: 1.5 }}>
            <Box className="treasury-q" itemProp="mainEntity" itemScope itemType="https://schema.org/Question">
              <Box className="Item-Head" sx={{ borderBottom: '1px solid #eae9ee' }}>
                <Typography component="h2" className="option-title" sx={{ mr: 'auto' }} itemProp="name">
                  What is the US Treasury yield curve?
                </Typography>
              </Box>
              <Box className="treasury-a" sx={{ borderBottom: '0 !important' }} itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                <Typography variant="caption" component="p"
                  className="option-title-desc" sx={{ p: 2, color: '#333', fontSize: '.875rem' }}
                  itemProp="text"
                >
                  {`As the name implies, a yield curve is a curve (chart) that shows the yields asked by the investors to buy US Treasury Bonds. It has bond maturities on the x-axis and the yields (interest rates) on the y-axis. The short-term end of the curve refers to the yields the US treasury has to pay on one-month to two-year maturity debt. The long-term end refers to the yields for longer maturities, such as 10-year to 30-year bonds. US treasury yields are also called "risk-free" rates, as no one expects the US govt to default.`}
                </Typography>
              </Box>
              {/* <meta itemprop="dateModified" content={dateModified} /> */}
            </Box>
          </Item>
          <Item className="row sum-r yield-invert" sx={{ my: 1.5 }}>
            <Box className="investors-q" itemProp="mainEntity" itemScope itemType="https://schema.org/Question">
              <Box className="Item-Head" sx={{ borderBottom: '1px solid #eae9ee' }}>
                <Typography component="h2" className="option-title" sx={{ mr: 'auto' }} itemProp="name">
                  Why do smart investors follow the yield curve?
                </Typography>
              </Box>
              <Box className="investors-a" sx={{ borderBottom: '0 !important' }} itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                <Typography variant="caption" component="p"
                  className="option-title-desc" sx={{ p: 2, color: '#333', fontSize: '.875rem' }}
                  itemProp="text"
                >
                  {`Yield Curve is a snapshot of the investor's view of the markets. It shows how the investor's expect markets to behave in the short-term as well as the long-term. Usually, the short-term rates are lesser than the long-term rates as the investors want to be compensated more for risk of giving their money for longer duration. However, sometimes, the yield curve "inverts". In other words, the short-term yields are higher than longer-term yields. Historically, a yield curve inversion has always preceded a "recession".`}
                </Typography>
              </Box>
              {/* <meta itemprop="dateModified" content={dateModified} /> */}
            </Box>
          </Item>
          <Item className="row sum-r yield-invert" sx={{ my: 1.5 }}>
            <Box className="recession-q" itemProp="mainEntity" itemScope itemType="https://schema.org/Question">
              <Box className="Item-Head" sx={{ borderBottom: '1px solid #eae9ee' }}>
                <Typography component="h2" className="option-title" sx={{ mr: 'auto' }} itemProp="name">
                  Why do inverted yield curve indicate recession?
                </Typography>
              </Box>
              <Box className="recession-a" sx={{ borderBottom: '0 !important' }} itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                <Typography variant="caption" component="p"
                  className="option-title-desc" sx={{ p: 2, color: '#333', fontSize: '.875rem' }}
                  itemProp="text"
                >
                  {`It's very simple. All business need loans to grow their businesses. The loans that they take are "short-term" loans typically to build factories, buildings, buy inputs, manufacture finished goods, marketing and several other capital expenditures and expenses. These expenses are expected to provide revenues in the future, which typically arrive in longer duration. So a typical, business takes on short-term risk (loan) for long-term (rewards).`}
                  <br />
                  <br />
                  {`If the yield curve is upward sloping, then the long-term rewards are higher than the short-term borrowing costs. So, it makes sense to grow business and deploy capital. However, if the short-term borrowing costs are high and long-term returns are low, then a lot of projects do not make economic sense and hence the business put a break on growth and capital spend. This in turn leads to hiring freezes. If short term rates remain higher for long period, then companies expenses rise, and they may also end up laying off excess employees.`}
                </Typography>
              </Box>
              {/* <meta itemprop="dateModified" content={dateModified} /> */}
            </Box>
          </Item>
          {/* <meta itemprop="dateModified" content={dateModified} /> */}
        </div>
      </Box>
    </NoData >
  );
};

export default (YieldCurve);
